import clsx from 'clsx'
import * as React from 'react'
import { makeStyles } from 'tss-react/mui'

import * as DateFnsNs from 'date-fns'
import { formatCPFOrCNPJ } from 'src/utils/formatter-utils'
import lodash from 'src/utils/lodash'
import { classToFComponent, FCClassContext, LocaleProvider } from 'src/utils/views'
import { Logger, NOOP_VOID } from 'wdc-cube'

import Box from '@mui/material/Box'
import { type OnChangeFn, type PaginationState, type SortingState } from '@tanstack/react-table'
import {
    getIsRowSelected,
    MaterialReactTable,
    MRT_Column,
    MRT_ColumnSizingState,
    MRT_ShowHideColumnsButton,
    MRT_Updater,
    useMaterialReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_RowSelectionState,
    type MRT_TableInstance
} from 'material-react-table'

import { pluralize } from './helpers/tcm_view_utils'
import {
    mrtBodyCellSx,
    mrtBodyRowSx,
    mrtBottomToolbarSx,
    mrtFooterCellSx,
    mrtHeadCellSx,
    mrtPaperSx,
    mrtTopToolbarSx
} from './helpers/tcm_mrt'

import { IconButton, MenuItem, Tooltip } from '@mui/material'
import { TableQSAScope, type QSARow } from '../tcm_scopes'

import DownloadIcon from '@mui/icons-material/Download'
import RestoreIcon from '@mui/icons-material/Restore'
import StraightenIcon from '@mui/icons-material/Straighten'
import ConfirmExportDataLimit from 'src/main/views/components/ConfirmExportDataLimit'
import { Caption, CaptionCount, SpanNumber } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('TCM-VIEW-LIST-QSA')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        width: 0,
        alignSelf: 'stretch'
    }
})

let GRID_COLUMN_DEFS: MRT_ColumnDef<QSARow>[] = []

export type QsaListagemViewProps = {
    className?: string
    style?: React.CSSProperties
    scope: TableQSAScope
}

export class QsaListagemViewClass implements FCClassContext<QsaListagemViewProps> {
    scope!: TableQSAScope
    classes!: ReturnType<typeof useStyles>['classes']
    loaded = false
    loading = true
    mrtLocation = LocaleProvider.get().mrt
    table!: MRT_TableInstance<QSARow>
    data?: QSARow[]
    dataVer = 0
    rowSelection: MRT_RowSelectionState = {}

    onSyncState(props: QsaListagemViewProps) {
        initializeStatics()
        this.scope = props.scope
        this.loaded = this.scope.loaded
        this.loading = !this.loaded
        this.classes = useStyles().classes
    }

    render(props: QsaListagemViewProps): React.JSX.Element {
        const { classes, scope, loading: isLoading, rowSelection } = this

        if (!this.data || this.dataVer !== scope.datasetVersion) {
            this.data = [...scope.dataset]
            this.dataVer = scope.datasetVersion
            for (const selKey of Object.keys(rowSelection)) {
                delete rowSelection[selKey]
            }

            for (const row of this.data) {
                if (row.selected) {
                    rowSelection[this.getRowId(row)] = true
                }
            }
        }

        const table = (this.table = useMaterialReactTable({
            localization: this.mrtLocation,
            columns: GRID_COLUMN_DEFS,
            data: this.data,
            defaultDisplayColumn: { enableResizing: true },
            enableColumnResizing: true,
            enableFullScreenToggle: false,
            enableRowSelection: true,
            enableSelectAll: true,
            enableColumnOrdering: true,
            enableStickyHeader: true,
            enableGrouping: false,
            enableRowVirtualization: true,
            rowVirtualizerOptions: { overscan: 5 },
            enableRowNumbers: false,
            layoutMode: 'grid-no-grow',
            getRowId: this.getRowId,

            // Overall appearance
            muiTablePaperProps: {
                elevation: 0,
                className: clsx(classes.view, props.className),
                sx: mrtPaperSx
            },
            muiTopToolbarProps: { sx: mrtTopToolbarSx },
            muiBottomToolbarProps: { sx: mrtBottomToolbarSx },
            renderColumnActionsMenuItems: this.renderColumnActionsMenuItems,
            renderTopToolbarCustomActions: this.renderTopToolbarCustomActions,
            renderToolbarInternalActions: this.renderToolbarInternalActions,
            muiTableHeadCellProps: { sx: mrtHeadCellSx },
            muiTableBodyCellProps: { sx: mrtBodyCellSx },
            muiTableFooterCellProps: { sx: mrtFooterCellSx },
            // Filtering
            manualFiltering: true,
            enableGlobalFilter: false,
            onColumnFiltersChange: this.emitColumnFilters,
            onGlobalFilterChange: this.emitGlobalFilter,
            onRowSelectionChange: this.emitRowSelectionChange,
            // pagging
            manualPagination: true,
            rowCount: scope.rowCount,
            onPaginationChange: this.emitPaginationChange,
            onSortingChange: this.emitSortingChange,
            muiPaginationProps: {
                rowsPerPageOptions: [100, 1000, 10_000],
                showRowsPerPage: true,
                showLastButton: false
            },
            // Content
            muiTableContainerProps: { sx: { maxHeight: '600px' } },
            muiTableBodyRowProps: (props) => ({
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                onClick: (_event) => {
                    const { row } = props
                    const checked = !getIsRowSelected(props)
                    row.toggleSelected(checked)
                    //this.emitOnRowClick(row, checked)
                },
                sx: mrtBodyRowSx,
                style: {
                    cursor: 'pointer',
                    userSelect: 'none'
                }
            }),
            muiSelectCheckboxProps: (props) => ({
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                onChange: (_event, checked) => {
                    const { row } = props
                    row.toggleSelected(checked)
                    //this.emitOnRowClick(row, checked)
                }
            }),
            initialState: {
                density: 'compact',
                columnVisibility: {
                    cnpj: true,
                    socio_nome: true,
                    socio_cpf_or_cnpj: true,
                    entrada_sociedade_dta: true,
                    qualificacao: true,
                    endereco: true,
                    lnkin_url: false,
                    lnkin_titulo: false,
                    lnkin_perfil: false,
                    lnkin_duracao: false,
                    instgr_url: false,
                    instgr_user: false,
                    instgr_qtd_followers: false,
                    instgr_qtd_following: false
                },
                rowSelection
            },
            onColumnSizingChange: this.setColumnSizing,
            state: {
                isLoading,
                pagination: {
                    pageIndex: scope.pageIndex,
                    pageSize: scope.pageSize
                },
                sorting: this.scope.sorting,
                columnFilters: this.scope.filters,
                rowSelection,
                columnSizing: this.scope.columnSizing
            }
        }))

        const tableRec = table as unknown as Record<string, unknown>
        tableRec.$scope = scope

        return (
            <>
                <MaterialReactTable table={table} />
                <ConfirmExportDataLimit scope={this.scope.exportDataLimitScope} />
            </>
        )
    }

    getRowId = (originalRow: QSARow) => {
        return originalRow.key
    }

    retractAllRows = (table: MRT_TableInstance<QSARow>) => {
        const defaultSizing: Record<string, number> = {}
        table.getAllLeafColumns().forEach((col) => {
            if (col.getIsVisible()) {
                defaultSizing[col.id] = col.columnDef.size ?? 100
            }
        })
        table.setColumnSizing(defaultSizing)
    }

    expandAllRows = (table: MRT_TableInstance<QSARow>) => {
        const font = '14px Roboto, Helvetica, Arial, sans-serif'
        const padding = 32
        const newSizing: Record<string, number> = {}

        table.getAllLeafColumns().forEach((column) => {
            if (column.getIsVisible()) {
                const colId = column.id
                let maxWidth = getTextWidth(column.columnDef.header, font) + 40

                table.getPrePaginationRowModel().rows.forEach((row) => {
                    const cellWidth = getTextWidth(row.getValue(colId), font) + padding
                    if (cellWidth > maxWidth) {
                        maxWidth = cellWidth
                    }
                })

                newSizing[colId] = maxWidth
            }
        })

        table.setColumnSizing(newSizing)
    }

    renderToolbarInternalActions = ({ table }: { table: MRT_TableInstance<QSARow> }) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    columnGap: 1
                }}
            >
                <MRT_ShowHideColumnsButton table={table} />

                <Tooltip title="Expandir todas as colunas">
                    <IconButton edge="start" color="inherit" onClick={() => this.expandAllRows(table)}>
                        <StraightenIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Restaurar tamanho das colunas">
                    <IconButton edge="start" color="inherit" onClick={() => this.retractAllRows(table)}>
                        <RestoreIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {this.scope.downloadEnabled && (
                    <Tooltip title="Download dos dados">
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => {
                                const columns = table
                                    .getVisibleLeafColumns()
                                    .map((c) => c.id)
                                    .filter((c) => !c.startsWith('mrt-row'))
                                this.emitDownloadClick(columns)
                            }}
                        >
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        )
    }

    renderTopToolbarCustomActions = () => {
        return (
            <div>
                <Caption>{this.scope.caption}</Caption>
                <CaptionCount>{pluralize(this.scope.rowCount, 'sócio encontrado', 'sócios encontrados')}</CaptionCount>
            </div>
        )
    }

    renderColumnActionsMenuItems = ({
        closeMenu,
        column,
        table,
        internalColumnMenuItems
    }: {
        closeMenu: () => void
        column: MRT_Column<QSARow>
        internalColumnMenuItems: React.ReactNode[]
        table: MRT_TableInstance<QSARow>
    }): React.ReactNode[] => {
        const items: React.ReactNode[] = []

        const font = '14px Roboto, Helvetica, Arial, sans-serif'
        const padding = 32
        const tolerance = 2

        const getIdealColumnWidth = () => {
            const colId = column.id

            const headerText = typeof column.columnDef.header === 'string' ? column.columnDef.header : colId

            let maxWidth = getTextWidth(headerText, font) + 40

            table.getRowModel().rows.forEach((row) => {
                const width = getTextWidth(row.getValue(colId), font) + padding
                if (width > maxWidth) {
                    maxWidth = width
                }
            })

            return Math.ceil(maxWidth)
        }

        const idealWidth = getIdealColumnWidth()
        const currentWidth = column.getSize()
        const isAlreadyExpanded = currentWidth >= idealWidth - tolerance

        internalColumnMenuItems.forEach((item) => {
            items.push(item)

            if (
                React.isValidElement<{ label?: unknown }>(item) &&
                typeof item.props.label === 'string' &&
                item.props.label.includes('Restaurar tamanho da coluna')
            ) {
                items.push(
                    <MenuItem
                        key={`auto-fit-column-${column.id}`}
                        title="Ajusta a largura ao maior conteúdo visível"
                        disabled={isAlreadyExpanded}
                        onClick={() => {
                            table.setColumnSizing((prev) => ({
                                ...prev,
                                [column.id]: idealWidth
                            }))
                            closeMenu()
                        }}
                    >
                        <div style={{ minWidth: '36px', marginLeft: '1px' }}>
                            <StraightenIcon fontSize="small" />
                        </div>
                        Expandir esta coluna
                    </MenuItem>
                )
            }
        })

        return items
    }

    emitRowSelectionChange: OnChangeFn<MRT_RowSelectionState> = (stateUpdater) => {
        if (lodash.isFunction(stateUpdater)) {
            this.rowSelection = stateUpdater(this.rowSelection)
            //this.scope.update()
            this.scope.onSelectionChange(new Map(Object.entries(this.rowSelection))).catch(LOG.caught)
        }
    }

    emitColumnFilters: OnChangeFn<MRT_ColumnFiltersState> = (filterStateUpdater) => {
        if (lodash.isFunction(filterStateUpdater)) {
            const newFiler = filterStateUpdater(this.scope.filters)
            this.scope.onFiltrationChange(newFiler).catch(LOG.caught)
        }
    }

    emitGlobalFilter = () => {
        LOG.debug('emitGlobalFilter: Nao deveria estar ativo')
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    emitPaginationChange: OnChangeFn<PaginationState> = (pageStateUpdater) => {
        if (lodash.isFunction(pageStateUpdater)) {
            const newState = pageStateUpdater({
                pageIndex: this.scope.pageIndex,
                pageSize: this.scope.pageSize
            })

            this.scope.onPaginationChange(newState.pageIndex, newState.pageSize).catch(LOG.caught)
        }
    }

    emitSortingChange: OnChangeFn<SortingState> = (pageStateUpdater) => {
        if (lodash.isFunction(pageStateUpdater)) {
            const newSort = pageStateUpdater(this.scope.sorting)
            this.scope.onSortingChange(newSort)
        }
    }

    readonly emitDownloadClick = (columns: string[]) => this.scope.onDownloadClick(columns).catch(LOG.caught)
    readonly setColumnSizing = (columns: MRT_Updater<MRT_ColumnSizingState>) => {
        if (typeof columns === 'function') {
            const current = this.scope.columnSizing || {}
            const newSizing = columns(current)
            this.scope.setColumnSizing(newSizing).catch(LOG.caught)
        } else {
            this.scope.setColumnSizing(columns).catch(LOG.caught)
        }
    }
}

export default classToFComponent(QsaListagemViewClass, React)

// :: Internal

let initializeStatics = () => {
    GRID_COLUMN_DEFS = static_newGridColumnDefs()
    initializeStatics = NOOP_VOID
}

function static_newGridColumnDefs(): MRT_ColumnDef<QSARow>[] {
    return [
        {
            enableColumnFilter: false,
            accessorKey: 'cnpj',
            header: 'CNPJ',
            size: 140,
            enableHiding: false
        },
        {
            enableColumnFilter: false,
            accessorKey: 'empresa_razao',
            header: 'Nome Empresa',
            size: 250
        },
        {
            enableColumnFilter: false,
            accessorKey: 'socio_nome',
            header: 'Nome',
            size: 250
        },
        {
            enableColumnFilter: false,
            accessorKey: 'socio_cpf_or_cnpj',
            header: 'CPF/CNPJ',
            size: 140,
            Cell: ({ cell }) => {
                const val = cell.getValue<string | null | undefined>()
                return <span>{formatCPFOrCNPJ(val)}</span>
            }
        },
        {
            enableColumnFilter: false,
            accessorKey: 'entrada_sociedade_dta',
            header: 'Entrada Sociedade',
            size: 140,
            filterVariant: 'date-range',
            Cell: ({ cell }) => {
                const val = cell.getValue<Date | null | undefined>()
                if (!val) {
                    return <span></span>
                }

                return <SpanNumber>{DateFnsNs.formatDate(val, 'dd/MM/yyyy')}</SpanNumber>
            }
        },
        {
            enableColumnFilter: false,
            accessorKey: 'qualificacao',
            header: 'Qualificação',
            size: 100
        },
        {
            enableColumnFilter: false,
            accessorKey: 'endereco',
            header: 'Endereço',
            size: 450
        },
        {
            enableColumnFilter: false,
            accessorKey: 'lnkin_url',
            header: 'LinkedIn URL',
            size: 130
        },
        {
            accessorKey: 'lnkin_titulo',
            header: 'LinkedIn Título',
            size: 130
        },
        {
            accessorKey: 'lnkin_perfil',
            header: 'LinkedIn Perfil',
            size: 130
        },
        {
            accessorKey: 'lnkin_duracao',
            header: 'LinkedIn Duração',
            size: 130
        },
        {
            enableColumnFilter: false,
            accessorKey: 'instgr_url',
            header: 'Instagram URL',
            size: 130
        },
        {
            accessorKey: 'instgr_user',
            header: 'Instagram Usuário',
            size: 130
        },
        {
            accessorKey: 'instgr_qtd_followers',
            header: 'Instagram Qtd. Seguidores',
            size: 120
        },
        {
            accessorKey: 'instgr_qtd_following',
            header: 'Instagram Qtd. Seguindo',
            size: 120
        }
    ]
}

const getTextWidth = (value: unknown, font: string) => {
    const text = value instanceof Date ? DateFnsNs.formatDate(value, 'dd/MM/yyyy HH:mm:ss') : String(value ?? '')
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (context) {
        context.font = font
        return context.measureText(text).width
    }
    return 0
}
